import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { api } from '@/lib/api';
import { useSession } from '@/app/session';
import { AuthLayout } from './AuthLayout';
import { useAuthForm } from './useAuthForm';
import { Button } from '@/ui/Button';
import { Field, TextInput } from '@/ui/Field';
import { Notice } from '@/ui/Panel';

/**
 * Signup creates the account and its org together. A coach never sees the word
 * "organisation": they name the club or team they coach, and their own name is a
 * valid answer for an independent coach.
 */
export function Signup({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { refresh } = useSession();
  const { busy, formError, fieldError, submit } = useAuthForm();
  const [form, setForm] = useState({ name: '', orgName: '', email: '', password: '' });

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AuthLayout
      locale={locale}
      title={t('auth.signupTitle')}
      lead={t('auth.signupLead')}
      footer={
        <>
          <span className="text-ink-muted">{t('auth.haveAccount')}</span>{' '}
          <Link to={localePath(locale, '/login')} className="font-medium underline decoration-line-strong underline-offset-2 hover:decoration-ink">
            {t('auth.signIn')}
          </Link>
        </>
      }
    >
      <form
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          const ok = await submit(async () => {
            await api.post('/auth/signup', {
              ...form,
              locale,
              // The browser knows where the coach is; a season of fixtures in the
              // wrong timezone is a real problem, so do not default to UTC blindly.
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            });
            await refresh();
          });
          if (ok) navigate(localePath(locale, '/'), { replace: true });
        }}
      >
        {formError ? (
          <div className="mb-4">
            <Notice tone="risk">{formError}</Notice>
          </div>
        ) : null}

        <Field label={t('auth.name')} error={fieldError('name')}>
          {(p) => (
            <TextInput {...p} value={form.name} onChange={set('name')} autoComplete="name" required autoFocus />
          )}
        </Field>

        <Field
          label={t('auth.orgName')}
          hint={t('auth.orgNameHint')}
          error={fieldError('orgName')}
        >
          {(p) => (
            <TextInput {...p} value={form.orgName} onChange={set('orgName')} autoComplete="organization" required />
          )}
        </Field>

        <Field label={t('auth.email')} error={fieldError('email')}>
          {(p) => (
            <TextInput
              {...p}
              type="email"
              value={form.email}
              onChange={set('email')}
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
            />
          )}
        </Field>

        <Field
          label={t('auth.password')}
          hint={t('auth.passwordHint')}
          error={fieldError('password')}
        >
          {(p) => (
            <TextInput
              {...p}
              type="password"
              value={form.password}
              onChange={set('password')}
              autoComplete="new-password"
              minLength={10}
              required
            />
          )}
        </Field>

        <Button type="submit" variant="primary" busy={busy} className="mt-2 w-full">
          {t('auth.signUp')}
        </Button>
      </form>
    </AuthLayout>
  );
}
